import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Search, X, ShieldCheck, CreditCard, Landmark } from "lucide-react";
import SectionCard from "../components/agentProgram/SectionCard";
import StatusPill from "../components/agentProgram/StatusPill";

const API = process.env.REACT_APP_API_URL;

export default function AgentKycVerification() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [agentId, setAgentId] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const searchBoxRef = useRef(null);

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  // Debounced agent-name search — searches Merchant name/identifier via the
  // existing merchant list endpoint, so nobody has to know a raw Mongo ID.
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/merchants`, {
          ...authConfig,
          params: { q: searchTerm.trim(), limit: 8 },
        });
        setSearchResults(data?.items || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectAgent = (merchant) => {
    setSelectedAgent(merchant);
    setAgentId(merchant._id);
    setSearchTerm("");
    setSearchResults([]);
    setShowResults(false);
    loadKyc(merchant._id);
  };

  const clearAgent = () => {
    setSelectedAgent(null);
    setAgentId("");
    setKyc(null);
  };

  const loadKyc = async (idOverride) => {
    const id = (idOverride || agentId).trim();
    setError("");
    setMessage("");
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/agent-program/kyc/${id}`, authConfig);
      setKyc(data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load KYC");
    }
  };

  const save = async (fields) => {
    setError("");
    setMessage("");
    try {
      const form = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) form.append(key, value);
      });
      const { data } = await axios.put(`${API}/agent-program/kyc/${agentId.trim()}`, form, {
        headers: { ...authConfig.headers, "Content-Type": "multipart/form-data" },
      });
      setKyc(data);
      setMessage("KYC updated.");
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to update KYC");
    }
  };

  const k = kyc || {};

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={21} className="text-emerald-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent KYC Verification</h1>
          <p className="text-sm text-gray-500 mt-0.5">No payout can be released until PAN and bank account are verified.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
        {selectedAgent ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">{selectedAgent.name}</div>
              {selectedAgent.identifier && (
                <div className="text-xs text-gray-500">{selectedAgent.identifier}</div>
              )}
            </div>
            <button
              onClick={clearAgent}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg border border-gray-200"
            >
              <X size={14} /> Change agent
            </button>
          </div>
        ) : (
          <div className="relative" ref={searchBoxRef}>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="border border-gray-200 rounded-lg text-sm pl-9 pr-3 py-2 w-full"
                placeholder="Search agent by name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
              />
            </div>
            {showResults && searchTerm.trim() && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searching ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Searching…</div>
                ) : searchResults.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">No agents match "{searchTerm}"</div>
                ) : (
                  searchResults.map((m) => (
                    <button
                      key={m._id}
                      onClick={() => selectAgent(m)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="text-sm text-gray-800">{m.name}</div>
                      {(m.identifier || m.contactNumber) && (
                        <div className="text-xs text-gray-400">
                          {[m.identifier, m.contactNumber].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">{message}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}

      {kyc && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <SectionCard
            icon={CreditCard}
            title="PAN"
            headerRight={<StatusPill label={k.panVerified ? "Verified" : "Unverified"} tone={k.panVerified ? "success" : "warning"} />}
          >
            <label className="block mb-4">
              <span className="block text-xs font-medium text-gray-600 mb-1">PAN Number</span>
              <input
                className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full uppercase"
                defaultValue={k.panNumber || ""}
                onBlur={(e) => save({ panNumber: e.target.value.toUpperCase() })}
              />
            </label>
            <button
              onClick={() => save({ panVerified: !k.panVerified })}
              className={`text-sm px-4 py-2 rounded-lg text-white shadow-sm hover:shadow transition-all ${k.panVerified ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            >
              {k.panVerified ? "Mark Unverified" : "Mark Verified"}
            </button>
          </SectionCard>

          <SectionCard
            icon={Landmark}
            title="Bank Account"
            headerRight={<StatusPill label={k.bankAccountVerified ? "Verified" : "Unverified"} tone={k.bankAccountVerified ? "success" : "warning"} />}
          >
            <div className="grid grid-cols-1 gap-3 mb-4">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Account Holder Name</span>
                <input
                  className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full"
                  defaultValue={k.bankAccountHolderName || ""}
                  onBlur={(e) => save({ bankAccountHolderName: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Account Number</span>
                <input
                  className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full"
                  defaultValue={k.bankAccountNumber || ""}
                  onBlur={(e) => save({ bankAccountNumber: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">IFSC</span>
                <input
                  className="border border-gray-200 rounded-lg text-sm px-3 py-2 w-full uppercase"
                  defaultValue={k.bankIfsc || ""}
                  onBlur={(e) => save({ bankIfsc: e.target.value.toUpperCase() })}
                />
              </label>
            </div>
            <button
              onClick={() => save({ bankAccountVerified: !k.bankAccountVerified })}
              className={`text-sm px-4 py-2 rounded-lg text-white shadow-sm hover:shadow transition-all ${k.bankAccountVerified ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            >
              {k.bankAccountVerified ? "Mark Unverified" : "Mark Verified"}
            </button>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
